// FAMILY-EXT: feature 1 + 2 — выбор периода скрытия + ввод текста "чего/от
// кого жду". Поддерживает пресеты, кастомную дату и время (default 10:00).
// onUpdate({ hiddenUntil, waitingReason }) при выборе пресета или Применить;
// ({ hiddenUntil: null }) при "показать сейчас" — текст причины при этом
// сохраняется в БД для истории.
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import TextareaAutosize from 'react-textarea-autosize';
import { Button, Form, Menu, TextArea } from 'semantic-ui-react';
import { Popup } from '../../lib/custom-ui';

import styles from './HideUntilStep.module.scss';

const addMinutes = (m) => {
  const d = new Date();
  d.setMinutes(d.getMinutes() + m);
  return d.toISOString();
};

const addHours = (h) => {
  const d = new Date();
  d.setHours(d.getHours() + h);
  return d.toISOString();
};

const addDays = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString();
};

const tomorrowMorning = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
};

const PRESETS = [
  // FAMILY-EXT: временный preset для проверки авто-возврата (звук + matrix).
  // Сервер unhide-scheduler опрашивает раз в минуту → возврат через ~1-2 мин.
  { label: '1 минута', getValue: () => addMinutes(1) },
  { label: '1 час', getValue: () => addHours(1) },
  { label: '4 часа', getValue: () => addHours(4) },
  { label: 'До завтра 9:00', getValue: tomorrowMorning },
  { label: '1 неделя', getValue: () => addDays(7) },
];

const HideUntilStep = React.memo(
  ({ defaultValue, defaultWaitingReason, onUpdate, onBack, onClose }) => {
    const isHidden = !!defaultValue && defaultValue > new Date().toISOString();
    const [reason, setReason] = useState(defaultWaitingReason || '');
    const [customDateTime, setCustomDateTime] = useState(null);

    const submit = useCallback(
      (hiddenUntil) => {
        const cleaned = reason.trim();
        onUpdate({
          hiddenUntil,
          waitingReason: cleaned === '' ? null : cleaned,
        });
        onClose();
      },
      [reason, onUpdate, onClose],
    );

    const handlePresetClick = useCallback(
      (getValue) => {
        submit(getValue());
      },
      [submit],
    );

    const handleShowNowClick = useCallback(() => {
      onUpdate({ hiddenUntil: null });
      onClose();
    }, [onUpdate, onClose]);

    // FAMILY-EXT: при первом выборе даты в календаре, если время не задано,
    // подставляем 10:00.
    const handleCustomDateChange = useCallback((d) => {
      if (d) {
        const next = new Date(d);
        if (
          next.getHours() === 0 &&
          next.getMinutes() === 0 &&
          next.getSeconds() === 0
        ) {
          next.setHours(10, 0, 0, 0);
        }
        setCustomDateTime(next);
      } else {
        setCustomDateTime(null);
      }
    }, []);

    const handleApplyCustom = useCallback(() => {
      if (!customDateTime) return;
      submit(customDateTime.toISOString());
    }, [customDateTime, submit]);

    return (
      <>
        <Popup.Header onBack={onBack}>Скрыть карточку на…</Popup.Header>
        <Popup.Content>
          {isHidden && (
            <div className={styles.currentlyHidden}>
              Скрыта до {new Date(defaultValue).toLocaleString('ru-RU')}
              {defaultWaitingReason && (
                <div className={styles.currentReason}>
                  Жду: {defaultWaitingReason}
                </div>
              )}
              <Button
                size="tiny"
                fluid
                positive
                className={styles.showNowButton}
                onClick={handleShowNowClick}
              >
                Показать сейчас
              </Button>
            </div>
          )}

          <Form className={styles.reasonForm}>
            <label className={styles.reasonLabel} htmlFor="family-waiting-reason">
              Чего/от кого жду?
            </label>
            <TextArea
              id="family-waiting-reason"
              as={TextareaAutosize}
              minRows={5}
              placeholder="Например: ответ от Иванова, поставка от поставщика…"
              value={reason}
              onChange={(_, { value }) => setReason(value)}
              className={styles.reasonField}
            />
          </Form>

          <div className={styles.menuLabel}>Пресеты:</div>
          <Menu secondary vertical className={styles.menu}>
            {PRESETS.map((preset) => (
              <Menu.Item
                key={preset.label}
                className={styles.menuItem}
                onClick={() => handlePresetClick(preset.getValue)}
              >
                {preset.label}
              </Menu.Item>
            ))}
          </Menu>

          <div className={styles.menuLabel}>Или выбрать дату и время:</div>
          <div className={styles.calendar}>
            <DatePicker
              inline
              disabledKeyboardNavigation
              selected={customDateTime}
              onChange={handleCustomDateChange}
              minDate={new Date()}
            />
          </div>
          <div className={styles.timeRow}>
            <label htmlFor="family-hide-time" className={styles.timeLabel}>
              Время:
            </label>
            <input
              id="family-hide-time"
              type="time"
              className={styles.timeInput}
              step="600"
              value={
                customDateTime
                  ? `${String(customDateTime.getHours()).padStart(2, '0')}:${String(
                      customDateTime.getMinutes(),
                    ).padStart(2, '0')}`
                  : '10:00'
              }
              disabled={!customDateTime}
              onChange={(e) => {
                if (!customDateTime) return;
                const [h, m] = e.target.value.split(':').map(Number);
                const next = new Date(customDateTime);
                next.setHours(h || 0, m || 0, 0, 0);
                setCustomDateTime(next);
              }}
            />
          </div>
          <Button
            positive
            fluid
            disabled={!customDateTime}
            onClick={handleApplyCustom}
          >
            {customDateTime
              ? `Скрыть до ${customDateTime.toLocaleString('ru-RU')}`
              : 'Выберите дату'}
          </Button>
        </Popup.Content>
      </>
    );
  },
);

HideUntilStep.propTypes = {
  defaultValue: PropTypes.string,
  defaultWaitingReason: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

HideUntilStep.defaultProps = {
  defaultValue: null,
  defaultWaitingReason: null,
};

export default HideUntilStep;
